/**
 * Profile Settings Page (Story 6-6-6)
 * Allows users to view and edit their own profile information.
 */
import { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { profileService } from "../../services/profileService";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../../components/atoms/Button";
import { Input } from "../../components/atoms/Input";
import { Card } from "../../components/molecules/Card";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatDateTime } from "../../utils/date";
import { Link } from "react-router-dom";
import {
  UserIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import type { ProfileResponse, ProfileUpdateData } from "../../types/models";
import i18n from "../../i18n";

interface ProfileFormData {
  email: string;
  full_name: string;
}

export function Profile() {
  const { t } = useTranslation("settings");
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { user, setUser } = useAuthStore();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors },
    reset,
    setError: setFormError,
  } = useForm<ProfileFormData>();

  const canEditFullName = profile?.role === "admin";

  useEffect(() => {
    loadProfile();
    
    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await profileService.getProfile();
      setProfile(data);
      reset({
        email: data.email ?? "",
        full_name: data.full_name || "",
      });
      // Update auth store with latest profile data (including locale for i18n — Story 11.1)
      if (user) {
        const locale = data.locale ?? user.locale ?? "en";
        i18n.changeLanguage(locale);
        setUser({
          ...user,
          email: data.email,
          pseudoLogin: data.pseudo_login ?? null,
          fullName: data.full_name,
          locale,
        });
      }
    } catch (err) {
      const message = getApiErrorMessage(err, t("profile_load_error"));
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: ProfileFormData) => {
    setError(null);
    setFieldErrors({});
    setSuccessMessage(null);

    const pseudoOnly =
      profile &&
      (!profile.email || !String(profile.email).trim()) &&
      Boolean(profile.pseudo_login);

    if (pseudoOnly) {
      if (!canEditFullName) {
        setIsEditing(false);
        return;
      }
      setUpdating(true);
      try {
        const updateData: ProfileUpdateData = {
          full_name: data.full_name.trim() || null,
        };
        const updated = await profileService.updateProfile(updateData);
        setProfile(updated);
        setIsEditing(false);
        setSuccessMessage(t("profile_update_success"));
        if (user) {
          const locale = updated.locale ?? user.locale ?? "en";
          i18n.changeLanguage(locale);
          setUser({
            ...user,
            email: updated.email,
            pseudoLogin: updated.pseudo_login ?? null,
            fullName: updated.full_name,
            locale,
          });
        }
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err: unknown) {
        setError(getApiErrorMessage(err, t("profile_update_error")));
      } finally {
        setUpdating(false);
      }
      return;
    }

    // Validate email is not empty
    const trimmedEmail = data.email.trim();
    if (!trimmedEmail) {
      setFieldErrors({ email: t("profile_email_required") });
      setFormError("email", { type: "manual", message: t("profile_email_required") });
      return;
    }

    // Check if values have changed
    if (profile) {
      const emailChanged = trimmedEmail !== (profile.email ?? "");
      const fullNameChanged = canEditFullName
        ? (data.full_name.trim() || null) !== profile.full_name
        : false;
      
      if (!emailChanged && !fullNameChanged) {
        // No changes, just exit edit mode
        setIsEditing(false);
        return;
      }
    }

    setUpdating(true);
    try {
      const updateData: ProfileUpdateData = {
        email: trimmedEmail,
      };
      if (canEditFullName) {
        updateData.full_name = data.full_name.trim() || null;
      }

      const updated = await profileService.updateProfile(updateData);
      setProfile(updated);
      setIsEditing(false);
      setSuccessMessage(t("profile_update_success"));

      // Update auth store
      if (user) {
        const locale = updated.locale ?? user.locale ?? "en";
        i18n.changeLanguage(locale);
        setUser({
          ...user,
          email: updated.email,
          pseudoLogin: updated.pseudo_login ?? null,
          fullName: updated.full_name,
          locale,
        });
      }

      // Clear success message after 3 seconds
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      const axiosError = err as {
        response?: {
          status?: number;
          data?: { detail?: string; message?: string };
        };
      };

      // Handle 422 validation errors - standardize on 'detail' field
      if (axiosError.response?.status === 422) {
        const detail = axiosError.response.data?.detail || axiosError.response.data?.message || t("profile_validation_error");
        
        // Check if it's a duplicate email error
        if (detail.toLowerCase().includes("email") && detail.toLowerCase().includes("already exists")) {
          setFieldErrors({ email: t("profile_email_taken") });
          setFormError("email", { type: "manual", message: t("profile_email_taken") });
        } else {
          setError(detail);
        }
      } else {
        const message = getApiErrorMessage(err, t("profile_update_error"));
        setError(message);
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    setFieldErrors({});
    setSuccessMessage(null);
    // Reset form to original values
    if (profile) {
      reset({
        email: profile.email ?? "",
        full_name: profile.full_name || "",
      });
    }
  };

  if (loading && !profile) {
    return (
      <div className="max-w-2xl mx-auto py-6 px-4">
        <Link
          to="/settings"
          className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4 rtl-flip" />
          {t("profile_back")}
        </Link>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">
          {t("profile_page_title")}
        </h1>
        <Card className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/4" />
            <div className="h-10 bg-neutral-200 dark:bg-neutral-700 rounded" />
            <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/4" />
            <div className="h-10 bg-neutral-200 dark:bg-neutral-700 rounded" />
          </div>
        </Card>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-2xl mx-auto py-6 px-4">
        <Link
          to="/settings"
          className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4 rtl-flip" />
          {t("profile_back")}
        </Link>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">
          {t("profile_page_title")}
        </h1>
        <Card className="p-6">
          <div className="text-center py-8">
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">{error || t("profile_load_error")}</p>
            <Button variant="primary" onClick={loadProfile}>
              {t("btn_retry")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const pseudoOnly =
    (!profile.email || !String(profile.email).trim()) && Boolean(profile.pseudo_login);

  return (
    <div className="max-w-2xl mx-auto py-6 px-4">
      <Link
        to="/settings"
        className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 mb-4"
      >
        <ArrowLeftIcon className="w-4 h-4 rtl-flip" />
        {t("profile_back")}
      </Link>
      <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">
        {t("profile_page_title")}
      </h1>

      {successMessage && (
        <div className="mb-4 rounded-lg bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 p-3 flex items-center gap-2">
          <CheckIcon className="w-5 h-5 text-success-600 dark:text-success-400" />
          <p className="text-sm text-success-800 dark:text-success-200">{successMessage}</p>
        </div>
      )}

      {error && !Object.keys(fieldErrors).length && (
        <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      <Card className="p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
            <UserIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {t("profile_account_info_title")}
            </h2>
            <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
              {canEditFullName
                ? t("profile_account_info_desc_full")
                : t("profile_account_info_desc")}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Email or read-only login handle (FR142) */}
          <div>
            {pseudoOnly ? (
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                  {t("profile_login_handle_label")}
                </label>
                <div className="w-full min-h-[44px] px-3 py-2.5 text-base border border-neutral-300 dark:border-neutral-600 rounded-lg bg-neutral-100 dark:bg-neutral-600 font-mono text-neutral-900 dark:text-neutral-100">
                  {profile.pseudo_login}
                </div>
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  {t("profile_login_handle_readonly")}
                </p>
              </div>
            ) : (
              <Input
                label={t("profile_email_label")}
                type="email"
                {...register("email", {
                  required: t("profile_email_required"),
                  validate: (value) => {
                    const trimmed = value?.trim();
                    if (!trimmed) {
                      return t("profile_email_required");
                    }
                    const emailPattern =
                      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
                    if (!emailPattern.test(trimmed)) {
                      return t("profile_email_invalid");
                    }
                    return true;
                  },
                })}
                error={fieldErrors.email || formErrors.email?.message}
                disabled={!isEditing}
                className={!isEditing ? "bg-neutral-100 dark:bg-neutral-600 cursor-not-allowed" : ""}
              />
            )}
          </div>

          {/* Full Name */}
          <div>
            <Input
              label={t("profile_full_name_label")}
              type="text"
              {...register("full_name", {
                maxLength: {
                  value: 255,
                  message: t("profile_full_name_max"),
                },
              })}
              error={fieldErrors.full_name || formErrors.full_name?.message}
              disabled={!isEditing || !canEditFullName}
              className={
                !isEditing || !canEditFullName
                  ? "bg-neutral-100 dark:bg-neutral-600 cursor-not-allowed"
                  : ""
              }
            />
            {!canEditFullName && (
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                {t("profile_full_name_admin_only")}
              </p>
            )}
          </div>

          {/* Read-only fields */}
          <div className="space-y-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                {t("profile_role_label")}
              </label>
              <div className="w-full min-h-[44px] md:min-h-[40px] px-3 py-2.5 text-base border border-neutral-300 dark:border-neutral-600 rounded-lg bg-neutral-100 dark:bg-neutral-600 text-neutral-600 dark:text-neutral-400 cursor-not-allowed">
                {t(`role_${profile.role}`, { defaultValue: profile.role })}
              </div>
            </div>

            {/* Account Status */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                {t("profile_account_status_label")}
              </label>
              <div className="w-full min-h-[44px] md:min-h-[40px] px-3 py-2.5 text-base border border-neutral-300 dark:border-neutral-600 rounded-lg bg-neutral-100 dark:bg-neutral-600 text-neutral-600 dark:text-neutral-400 cursor-not-allowed">
                {profile.is_active ? t("profile_status_active") : t("profile_status_inactive")}
              </div>
            </div>

            {/* Created At */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                {t("profile_created_at_label")}
              </label>
              <div className="w-full min-h-[44px] md:min-h-[40px] px-3 py-2.5 text-base border border-neutral-300 dark:border-neutral-600 rounded-lg bg-neutral-100 dark:bg-neutral-600 text-neutral-600 dark:text-neutral-400 cursor-not-allowed">
                {formatDateTime(profile.created_at)}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 pt-4">
            {isEditing ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancel}
                  className="flex-1"
                >
                  <XMarkIcon className="w-4 h-4 me-2" />
                  {t("btn_cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={updating}
                  disabled={updating}
                  className="flex-1"
                >
                  <CheckIcon className="w-4 h-4 me-2" />
                  {t("profile_save_changes")}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="primary"
                onClick={() => setIsEditing(true)}
                className="flex-1"
              >
                <PencilIcon className="w-4 h-4 me-2" />
                {t("profile_edit")}
              </Button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
}
